import {FormEvent, useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../Store/Store';
import {toast} from 'react-hot-toast';
import {knowledgebaseService} from '../Services/knowledgebaseService';
import {Knowledgebase, KnowledgebaseDocumentStatus, KnowledgebaseFormData} from '../Types';
import {setFileDlpModalState} from '../Store/Actions/modalActions';
import {FileDlpModalDataOrigin} from '../Types/StoreTypes';
import useGlobals from './useGlobals';

export default function useKnowledgebase() {
    const {id} = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const i18n = useSelector((state: RootState) => state.i18n.i18n);
    const [isLoaded, setIsLoaded] = useState(!id || id === 'new');
    const [isSaving, setIsSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(id !== undefined && id !== 'new');
    const [knowledgebaseSnapshot, setKnowledgebaseSnapshot] = useState<Knowledgebase | null>(null);
    const [fileDuplicates, setFileDuplicates] = useState<{
        [key: string]: { id: string; name: string; sha1: string }[];
    }>({});
    const [refreshCount, setRefreshCount] = useState<number>(0)
    const {demo} = useGlobals();
    const {embeddingModelDlp} = useSelector((state: RootState) => state.settings);

    const [formData, setFormData] = useState<KnowledgebaseFormData>({
        name: '',
        description: '',
        documents: [],
        documentsToRemove: [],
        documentsToReembed: [],
    });

    const handleChange = (name: string, value: string | File[]) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDocumentUpload = async (files: File[]) => {
        const maxFileSize = 256 * 1024 * 1024;
        let failed = false;

        try {
            const uploadPromises = Array.from(files).map(async (blob) => {
                if (blob.size > maxFileSize) {
                    toast.error(
                        i18n.translate({
                            id: 'fileUpload.error.fileSize',
                            sourceName: 'FileUpload',
                            params: {
                                fileName: blob.name,
                            },
                        }),
                    );
                    failed = true;
                    return;
                }

                const file = new File([blob], blob.name);
                const formData = new FormData();
                formData.append('file', file);

                try {
                    const response = await knowledgebaseService.uploadFile(formData);
                    if (response.data.error) {
                        console.error(response.data.error);
                        toast.error(
                            i18n.translate({
                                id: 'fileUpload.error.upload',
                                sourceName: 'FileUpload',
                                params: {
                                    fileName: blob.name,
                                },
                            }),
                        );
                        failed = true;
                        return;
                    }
                    return {
                        name: response.data.name,
                        sha1: response.data.sha1,
                    };
                } catch (error) {
                    toast.error(
                        i18n.translate({
                            id: 'fileUpload.error.upload',
                            sourceName: 'FileUpload',
                            params: {
                                fileName: blob.name,
                            },
                        }),
                    );
                    console.error('File upload error:', error);
                    return null;
                }
            });

            const results = await Promise.all(uploadPromises);
            const successfulUploads = results.filter(
                (result): result is { name: string; sha1: string } => result !== null,
            );

            if (successfulUploads.length > 0 && !failed) {
                dispatch(
                    setFileDlpModalState({
                        state: 'open',
                        files: successfulUploads,
                        combinedDlp: embeddingModelDlp,
                        origin: FileDlpModalDataOrigin.Knowledgebase,
                        callback: async (files: any[]) => {
                            if(files.length >= 1){
                                const documents = [...formData.documents, ...files]
                                let existingDocuments = []
                                if(id && id !== 'new'){
                                    const res = await knowledgebaseService.checkDocumentsExistence(id as string, documents.map(doc => doc.sha1))
                                    existingDocuments = documents.filter(doc => res.data.includes(doc.sha1))
                                }

                                const duplicates: Record<string, any[]> = existingDocuments.reduce((acc, doc) => ({...acc, [doc.sha1]: [doc]}), {})
                                const uniqueDocuments: { name: string, sha1: string }[] = []
                                for (const doc of documents) {
                                    const key: string = doc.sha1 || doc.name || JSON.stringify(doc);
                                    if (!duplicates[key]) {
                                        uniqueDocuments.push(doc)
                                    }
                                    duplicates[key] = (duplicates[key] ?? []).concat([doc]);
                                }
                                setFileDuplicates(Object.keys(duplicates).reduce((acc, sha1) => (duplicates[sha1].length === 1 ? acc : {...acc, [sha1]: duplicates[sha1]}), {}))

                                setFormData((prev) => ({
                                    ...prev,
                                    documents: uniqueDocuments
                                }));
                            }
                        },
                    }),
                );
            }
        } catch (error) {
            toast.error(
                i18n.translate({
                    id: 'fileUpload.error.model',
                    sourceName: 'FileUpload',
                }),
            );
            console.error('Model loading error:', error);
        }
    };

    const handleReembedDocument = (identifier: string) => {
        setFormData((prev) => ({
            ...prev,
            documentsToReembed: prev.documentsToReembed.includes(identifier) ? prev.documentsToReembed.filter(id => id !== identifier) : prev.documentsToReembed.concat([identifier]),
        }));
    }

    const handleRemoveExistingDocument = (identifier: string) => {
        setFormData((prev) => ({
            ...prev,
            documentsToRemove: prev.documentsToRemove.concat([identifier]),
        }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            if (isEditMode && knowledgebaseSnapshot) {
                await knowledgebaseService.updateKnowledgebase(knowledgebaseSnapshot.identifier, formData);
            } else {
                await knowledgebaseService.createKnowledgebase(formData);
            }

            toast.success(i18n.translate({ id: 'toast.success', sourceName: 'Knowledgebase' }));
            navigate('/admin/knowledgebases');
        } catch (error) {
            toast.error(i18n.translate({ id: 'error', sourceName: 'Main' }));
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (id && id !== 'new' && !isLoaded) {
            setIsEditMode(true);
            const loadKnowledgebase = async () => {
                const response = await knowledgebaseService.getKnowledgebase(id);
                setKnowledgebaseSnapshot(response.data);
                setFormData({
                    name: response.data.name,
                    description: response.data.description,
                    documents: [],
                    documentsToRemove: [],
                    documentsToReembed: [],
                });
                setIsLoaded(true);
            };
            loadKnowledgebase();
        } else if (id === 'new') {
            setIsEditMode(false);
            setIsLoaded(true);
        } else {
            setIsLoaded(true);
        }
    }, [id, formData, isLoaded, demo]);


    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (isEditMode && id && id !== 'new') {
            interval = setInterval(() => {
                setRefreshCount(refreshCount + 1)
            }, 10000);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [isEditMode, id, demo, refreshCount]);

    const getKnowledgebaseFiles = (formData: KnowledgebaseFormData, page: number, itemsPerPage: number, query?: string | null) => {
        if(formData.documents.length === 0) {
            return {data: {data: [], total: 0}}
        }

        let documents = formData.documents
        let terms: string[] = query==null ? [] : query.split(" ").map(t=>t.toLowerCase().trim()).filter(t=>t.length>=1)
        if(terms.length>=1) {
            documents = formData.documents.filter(f=> terms.some(t => f.name.toLowerCase().includes(t)))
        }
        return {data: {data: documents.sort((d1,d2)=> d1.name.localeCompare(d2.name)).slice(itemsPerPage*(page-1), itemsPerPage*(page-1) + itemsPerPage), total: documents.length}}
    }

    return {
        isEditMode,
        isLoaded,
        isSaving,
        formData,
        i18n,
        handleSubmit,
        handleChange,
        handleDocumentUpload,
        handleReembedDocument,
        handleRemoveExistingDocument,
        knowledgebaseSnapshot,
        fileDuplicates,
        setFileDuplicates,
        getKnowledgebaseDocuments: useMemo(()=> (async (status: KnowledgebaseDocumentStatus | null, page: number, itemsPerPage: number, _: any, query?: string | null) => {
            let fileRes: {data:{total: number, data: any[]}} = {data:{total: 0, data:[]}};
            if(status === null){
                fileRes = getKnowledgebaseFiles(formData, page, itemsPerPage, query)
            }
            // refreshCount is there to trigger automatic fetching (always >= 0)
            if (refreshCount >= 0 ) {
                const maxFilePageCount = Math.ceil(fileRes.data.total / itemsPerPage)
                page =  Math.max((maxFilePageCount >= 1 ? 1 : 0) + page - maxFilePageCount, 1);
                const shouldIncludeDocuments = itemsPerPage > fileRes.data.data.length

                const res = !id || id === 'new' ? {data:{total: 0, data:[]}} : await knowledgebaseService.getKnowledgebaseDocuments(id, status, page, itemsPerPage, query)

                return {
                    data: {
                        total: res.data.total + fileRes.data.total,
                        data: fileRes.data.data.concat(
                            !shouldIncludeDocuments ? [] : res.data.data.filter(doc => !formData.documentsToRemove.includes(doc.identifier))
                        )
                    }
                }
            }
        }), [formData.documentsToRemove, formData.documents, id, refreshCount]),
    };
}
